"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const store = require("@tanstack/store");
const asyncRetryer = require("./async-retryer.cjs");
const utils = require("./utils.cjs");
const eventClient = require("./event-client.cjs");
function getDefaultAsyncThrottlerState() {
  return {
    errorCount: 0,
    isExecuting: false,
    isPending: false,
    lastArgs: void 0,
    lastExecutionTime: 0,
    lastResult: void 0,
    maybeExecuteCount: 0,
    nextExecutionTime: void 0,
    settleCount: 0,
    status: "idle",
    successCount: 0
  };
}
function asyncThrottlerOptions(options) {
  return options;
}
const defaultOptions = {
  asyncRetryerOptions: {
    maxAttempts: 1
  },
  enabled: true,
  leading: true,
  trailing: true,
  wait: 0
};
class AsyncThrottler {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this.store = new store.Store(getDefaultAsyncThrottlerState());
    this.asyncRetryers = /* @__PURE__ */ new Map();
    this.#timeoutId = null;
    this.#resolvePreviousPromise = null;
    this._emit = () => eventClient.emitChange("AsyncThrottler", this);
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
      if (!this.#getEnabled()) {
        this.cancel();
      }
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const { isPending, isExecuting, settleCount } = combinedState;
        return {
          ...combinedState,
          status: !this.#getEnabled() ? "disabled" : isPending ? "pending" : isExecuting ? "executing" : settleCount > 0 ? "settled" : "idle"
        };
      });
      eventClient.emitChange("AsyncThrottler", this);
    };
    this.#getEnabled = () => {
      return !!utils.parseFunctionOrValue(this.options.enabled, this);
    };
    this.#getWait = () => {
      return utils.parseFunctionOrValue(this.options.wait, this);
    };
    this.maybeExecute = async (...args) => {
      if (!this.#getEnabled()) return void 0;
      this.#resolvePreviousPromiseInternal();
      this.#setState({
        maybeExecuteCount: this.store.state.maybeExecuteCount + 1,
        lastArgs: args
        // store the arguments for potential trailing execution
      });
      const wait = this.#getWait();
      const thisMaybeExecuteNumber = this.store.state.maybeExecuteCount;
      for (let maxNumIterations = wait / 10; this.store.state.isExecuting && maxNumIterations > 0; maxNumIterations--) {
        await new Promise((resolve) => setTimeout(resolve, 10));
        if (this.store.state.maybeExecuteCount !== thisMaybeExecuteNumber) {
          return this.store.state.lastResult;
        }
      }
      const now = Date.now();
      const timeSinceLastExecution = now - this.store.state.lastExecutionTime;
      if (this.options.leading && !this.store.state.isPending && timeSinceLastExecution >= wait) {
        await this.#execute(...args);
      } else if (this.options.trailing) {
        this.cancel();
        this.#setState({
          isPending: true
        });
        return new Promise((resolve, reject) => {
          this.#resolvePreviousPromise = resolve;
          const newTimeSinceLastExecution = this.store.state.lastExecutionTime ? now - this.store.state.lastExecutionTime : 0;
          const timeoutDuration = Math.max(0, wait - newTimeSinceLastExecution);
          this.#timeoutId = setTimeout(async () => {
            this.#clearTimeout();
            if (this.store.state.lastArgs !== void 0) {
              try {
                await this.#execute(...this.store.state.lastArgs);
              } catch (error) {
                reject(error);
              }
            }
            this.#resolvePreviousPromise = null;
            resolve(this.store.state.lastResult);
          }, timeoutDuration);
        });
      }
      return this.store.state.lastResult;
    };
    this.#execute = async (...args) => {
      if (!this.#getEnabled()) return void 0;
      const currentMaybeExecute = this.store.state.maybeExecuteCount;
      try {
        this.#setState({ isExecuting: true });
        const currentAsyncRetryer = new asyncRetryer.AsyncRetryer(this.fn, {
          ...this.options.asyncRetryerOptions,
          key: `${this.key}-retryer-${currentMaybeExecute}`
        });
        this.asyncRetryers.set(currentMaybeExecute, currentAsyncRetryer);
        const result = await currentAsyncRetryer.execute(...args);
        this.#setState({
          lastResult: result,
          successCount: this.store.state.successCount + 1
        });
        this.options.onSuccess?.(result, args, this);
      } catch (error) {
        this.#setState({
          errorCount: this.store.state.errorCount + 1
        });
        this.options.onError?.(error, args, this);
        if (this.options.throwOnError) {
          throw error;
        }
      } finally {
        this.asyncRetryers.delete(currentMaybeExecute);
        const lastExecutionTime = Date.now();
        const wait = this.#getWait();
        const nextExecutionTime = lastExecutionTime + wait;
        this.#setState({
          isExecuting: false,
          isPending: !!this.#timeoutId,
          settleCount: this.store.state.settleCount + 1,
          lastExecutionTime,
          nextExecutionTime
        });
        this.options.onSettled?.(args, this);
        setTimeout(() => {
          if (!this.store.state.isPending) {
            this.#setState({ nextExecutionTime: void 0 });
          }
        }, wait);
      }
      return this.store.state.lastResult;
    };
    this.flush = async () => {
      if (this.store.state.isPending && this.store.state.lastArgs) {
        const resolvePromise = this.#resolvePreviousPromise;
        this.#clearTimeout();
        this.#setState({
          isPending: false
        });
        const result = await this.#execute(...this.store.state.lastArgs);
        if (resolvePromise) {
          resolvePromise(result);
        }
        return result;
      }
      return void 0;
    };
    this.#resolvePreviousPromiseInternal = () => {
      if (this.#resolvePreviousPromise) {
        this.#resolvePreviousPromise(this.store.state.lastResult);
        this.#resolvePreviousPromise = null;
      }
    };
    this.#clearTimeout = () => {
      if (this.#timeoutId) {
        clearTimeout(this.#timeoutId);
        this.#timeoutId = null;
      }
    };
    this.abort = () => {
      this.asyncRetryers.forEach((retryer) => retryer.abort());
      this.asyncRetryers.clear();
      this.#setState({ isExecuting: false });
    };
    this.cancel = () => {
      this.#clearTimeout();
      if (this.#resolvePreviousPromise) {
        this.#resolvePreviousPromiseInternal();
        this.#resolvePreviousPromise = null;
      }
      this.#setState({
        isPending: false
      });
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncThrottlerState());
      this.asyncRetryers.forEach((retryer) => retryer.reset());
    };
    this.key = utils.createKey(initialOptions.key);
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? !initialOptions.onError
    };
    this.#setState(this.options.initialState ?? {});
    eventClient.pacerEventClient.on("d-AsyncThrottler", (event) => {
      if (event.payload.key !== this.key) return;
      this.#setState(event.payload.store.state);
      this.setOptions(event.payload.options);
    });
  }
  #timeoutId;
  #resolvePreviousPromise;
  #setState;
  #getEnabled;
  #getWait;
  #execute;
  #resolvePreviousPromiseInternal;
  #clearTimeout;
  /**
   * Returns the AbortSignal for a specific execution.
   * If no maybeExecuteCount is provided, returns the signal for the most recent execution.
   * Returns null if no execution is found or not currently executing.
   *
   * @param maybeExecuteCount - Optional specific execution to get signal for
   * @example
   * ```typescript
   * const throttler = new AsyncThrottler(
   *   async (data: string) => {
   *     const signal = throttler.getAbortSignal()
   *     if (signal) {
   *       const response = await fetch('/api/save', {
   *         method: 'POST',
   *         body: data,
   *         signal
   *       })
   *       return response.json()
   *     }
   *   },
   *   { wait: 1000 }
   * )
   * ```
   */
  getAbortSignal(maybeExecuteCount) {
    const count = maybeExecuteCount ?? this.store.state.maybeExecuteCount;
    const retryer = this.asyncRetryers.get(count);
    return retryer?.getAbortSignal() ?? null;
  }
}
function asyncThrottle(fn, initialOptions) {
  const asyncThrottler = new AsyncThrottler(fn, initialOptions);
  return asyncThrottler.maybeExecute;
}
exports.AsyncThrottler = AsyncThrottler;
exports.asyncThrottle = asyncThrottle;
exports.asyncThrottlerOptions = asyncThrottlerOptions;
//# sourceMappingURL=async-throttler.cjs.map
